import React, { useState, useEffect, useCallback } from 'react';
import { formatCurrency } from '../utils/currency';
import { fetchDeedInstruments, createDeedInstrument, updateDeedInstrument, deleteDeedInstrument } from '../api';
import { useConfirm } from './ConfirmProvider';
import ExaminerNotesList from './ExaminerNotesList';

const INSTRUMENT_TYPES = [
  ['DEED_OF_TRANSFER', 'Deed of Transfer'],
  ['MORTGAGE_BOND', 'Mortgage Bond'],
  ['BOND_CANCELLATION', 'Bond Cancellation'],
  ['POWER_OF_ATTORNEY', 'Power of Attorney'],
  ['NOTARIAL_BOND', 'Notarial Bond'],
  ['CONSENT_TO_CANCEL', 'Consent to Cancel Bond'],
  ['SECTIONAL_PLAN', 'Sectional Plan'],
  ['OTHER', 'Other'],
];

const STATUS_CONFIG = {
  DRAFTING:    { label: 'Drafting',        color: 'bg-gray-100 text-gray-700' },
  SIGNED:      { label: 'Signed',          color: 'bg-blue-100 text-blue-700' },
  LODGED:      { label: 'Lodged',          color: 'bg-yellow-100 text-yellow-700' },
  ON_PREP:     { label: 'On Prep',         color: 'bg-orange-100 text-orange-700' },
  REGISTERED:  { label: 'Registered',      color: 'bg-green-100 text-green-700' },
  REJECTED:    { label: 'Rejected',        color: 'bg-red-100 text-red-700' },
  WITHDRAWN:   { label: 'Withdrawn',       color: 'bg-gray-200 text-gray-500' },
};

const STATUS_ORDER = ['DRAFTING', 'SIGNED', 'LODGED', 'ON_PREP', 'REGISTERED'];

const BLANK = {
  instrument_type: 'DEED_OF_TRANSFER', status: 'DRAFTING',
  deeds_registry_reference: '', lodgement_date: '', registration_date: '',
  new_deed_number: '', amount: '', conveyancing_detail: '',
};

// Recommendation C.10: default the instrument Amount from the matter's own
// Property Value / Bond Amount instead of making the user retype a figure
// that's already on file — still editable, since real instrument amounts
// can genuinely differ from the headline figures.
const AMOUNT_SOURCE_BY_TYPE = {
  DEED_OF_TRANSFER: 'propertyValue',
  MORTGAGE_BOND: 'bondAmount',
  BOND_CANCELLATION: 'bondAmount',
  NOTARIAL_BOND: 'bondAmount',
  CONSENT_TO_CANCEL: 'bondAmount',
};

export default function DeedInstrumentsList({ authAxios, showFormMessage, conveyancingDetailId, propertyValue, bondAmount }) {
  const confirm = useConfirm();
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    if (!conveyancingDetailId) return;
    setLoading(true);
    try {
      const data = await fetchDeedInstruments(authAxios, conveyancingDetailId);
      setInstruments(Array.isArray(data) ? data : []);
    } catch {
      showFormMessage?.('error', 'Failed to load instruments.');
    } finally {
      setLoading(false);
    }
  }, [authAxios, conveyancingDetailId, showFormMessage]);

  useEffect(() => { load(); }, [load]);

  const defaultAmountFor = (instrumentType) => {
    const source = AMOUNT_SOURCE_BY_TYPE[instrumentType];
    if (source === 'propertyValue') return propertyValue || '';
    if (source === 'bondAmount') return bondAmount || '';
    return '';
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...BLANK, conveyancing_detail: conveyancingDetailId, amount: defaultAmountFor(BLANK.instrument_type) });
    setShowForm(true);
  };

  const openEdit = (ins) => {
    setEditing(ins);
    setForm({ ...BLANK, ...ins, conveyancing_detail: conveyancingDetailId });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.amount) payload.amount = null;
      if (!payload.lodgement_date) payload.lodgement_date = null;
      if (!payload.registration_date) payload.registration_date = null;

      if (editing) {
        await updateDeedInstrument(authAxios, editing.id, payload);
        showFormMessage?.('success', 'Instrument updated.');
      } else {
        await createDeedInstrument(authAxios, payload);
        showFormMessage?.('success', 'Instrument created.');
      }
      setShowForm(false);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail ?? JSON.stringify(err.response?.data ?? err.message);
      showFormMessage?.('error', `Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ins) => {
    const ok = await confirm({ title: 'Delete instrument?', message: `Delete ${ins.instrument_type_display ?? ins.instrument_type}?` });
    if (!ok) return;
    try {
      await deleteDeedInstrument(authAxios, ins.id);
      showFormMessage?.('success', 'Instrument deleted.');
      load();
    } catch {
      showFormMessage?.('error', 'Failed to delete instrument.');
    }
  };

  const fld = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800">Deed Instruments</h4>
        <button onClick={openCreate}
          className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700">
          + Add Instrument
        </button>
      </div>

      {loading && <p className="text-sm text-gray-400">Loading…</p>}

      <div className="space-y-3">
        {instruments.map(ins => {
          const sc = STATUS_CONFIG[ins.status] ?? { label: ins.status, color: 'bg-gray-100 text-gray-700' };
          const stepIdx = STATUS_ORDER.indexOf(ins.status);
          return (
            <div key={ins.id} className="border border-gray-200 rounded-xl p-4 bg-white space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-medium text-gray-800 text-sm">
                    {INSTRUMENT_TYPES.find(([v]) => v === ins.instrument_type)?.[1] ?? ins.instrument_type}
                  </div>
                  {ins.new_deed_number && (
                    <div className="text-xs text-gray-500 font-mono mt-0.5">No. {ins.new_deed_number}</div>
                  )}
                </div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>{sc.label}</span>
              </div>

              {/* Stage pipeline */}
              <div className="flex items-center gap-1">
                {STATUS_ORDER.map((s, i) => {
                  const done = stepIdx >= i;
                  return (
                    <React.Fragment key={s}>
                      <div className={`h-2 flex-1 rounded-full ${done ? 'bg-blue-500' : 'bg-gray-200'}`} />
                      {i < STATUS_ORDER.length - 1 && (
                        <div className={`w-0 h-0 border-t-4 border-b-4 border-l-4 border-transparent ${done ? 'border-l-blue-500' : 'border-l-gray-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="flex justify-between text-[10px] text-gray-400">
                {STATUS_ORDER.map(s => <span key={s}>{STATUS_CONFIG[s].label}</span>)}
              </div>

              <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                {ins.deeds_registry_reference && <span>Ref: <span className="font-mono">{ins.deeds_registry_reference}</span></span>}
                {ins.lodgement_date && <span>Lodged: {ins.lodgement_date}</span>}
                {ins.registration_date && <span>Registered: {ins.registration_date}</span>}
                {ins.amount && <span>{formatCurrency(ins.amount)}</span>}
              </div>

              <div className="flex gap-3 items-center justify-between">
                <button onClick={() => setExpandedId(expandedId === ins.id ? null : ins.id)}
                  className="text-xs text-orange-600 hover:underline font-medium">
                  {expandedId === ins.id ? '▾ Hide Examiner Notes' : '▸ Examiner Notes'}
                </button>
                <div className="flex gap-3">
                  <button onClick={() => openEdit(ins)} className="text-xs text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => handleDelete(ins)} className="text-xs text-red-500 hover:underline">Delete</button>
                </div>
              </div>

              {expandedId === ins.id && (
                <div className="mt-2 pl-3 border-l-2 border-orange-200">
                  <ExaminerNotesList authAxios={authAxios} showFormMessage={showFormMessage} instrumentId={ins.id} />
                </div>
              )}
            </div>
          );
        })}
        {!loading && instruments.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">No instruments yet. Add one above.</p>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-5 border-b flex items-center justify-between">
              <h3 className="font-semibold">{editing ? 'Edit Instrument' : 'New Instrument'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Instrument Type *</label>
                <select required value={form.instrument_type} onChange={e => {
                    const newType = e.target.value;
                    setForm(f => ({
                      ...f,
                      instrument_type: newType,
                      // Only re-default if the user hasn't typed their own amount yet.
                      amount: f.amount ? f.amount : defaultAmountFor(newType),
                    }));
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {INSTRUMENT_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
                <select value={form.status} onChange={e => fld('status', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                  {Object.entries(STATUS_CONFIG).map(([v, { label }]) => <option key={v} value={v}>{label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Deeds Registry Reference</label>
                <input value={form.deeds_registry_reference} onChange={e => fld('deeds_registry_reference', e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Lodgement Date</label>
                  <input type="date" value={form.lodgement_date} onChange={e => fld('lodgement_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Registration Date</label>
                  <input type="date" value={form.registration_date} onChange={e => fld('registration_date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">New Deed No. (T/B-number)</label>
                  <input value={form.new_deed_number} onChange={e => fld('new_deed_number', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Amount (BWP)</label>
                  <input type="number" step="0.01" value={form.amount} onChange={e => fld('amount', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                <button type="button" onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={saving}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-60">
                  {saving ? 'Saving…' : editing ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
